import * as React from "react";

import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { renderMarkdown } from "@impero-com/ui";
import { InputRef, Typography, Input } from "antd";

import {
    CustomizedValidation,
    EditFormCreate,
    FormComponentProps,
    ValidationRule,
    validationRulesConverter,
} from "utils/form";

import { BaseFormDisplay } from "./formDisplay";

import { BaseFormField, EditInput, EditProps } from ".";

const { Text } = Typography;

interface CreateStringInputProps<ID extends string, R>
    extends FormComponentProps<ID, string, R>, StringInputCommonProps {
    id: ID;
    initialValue: string | undefined;
    validators?: ValidationRule<string>[];
    creationMode?: boolean;
    empty?: boolean;
    validationError?: CustomizedValidation;
}

interface CreateStringInputState {
    editing: boolean;
}

class CreateStringInput<ID extends string, R> extends React.PureComponent<
    CreateStringInputProps<ID, R>,
    CreateStringInputState
> {
    private innerField = React.createRef<InputRef>();

    state: CreateStringInputState = {
        editing: false,
    };

    render = () => {
        const {
            form: { isFieldValidating, getFieldError },
            id,
            label,
            creationMode,
            validationError,
        } = this.props;
        const { editing } = this.state;
        const readonly =
            !creationMode &&
            !(editing || isFieldValidating(id) || getFieldError(id));

        return (
            <BaseFormField
                getComponent={this.getComponent}
                getReadonlyComponent={
                    !creationMode ? this.getReadonlyComponent : undefined
                }
                onClick={this.onLabelClick}
                hasFeedback={false}
                label={label}
                readonly={readonly}
                validationError={validationError}
            />
        );
    };

    getComponent = () => {
        const { form, id, validators, label, initialValue } = this.props;
        const { getFieldDecorator } = form;
        return getFieldDecorator(id, {
            rules: validationRulesConverter<string>()(
                id,
                form,
                validators,
                label
            ),
            valuePropName: "defaultValue",
            trigger: "onBlur",
            validateFirst: true,
            preserve: true,
            initialValue,
        })(
            <Input
                ref={this.innerField}
                type="text"
                onPressEnter={this.blur}
                onBlur={this.onBlur}
                onFocus={this.onFocus}
            />
        );
    };

    getReadonlyComponent = () => {
        const {
            form: { getFieldValue },
            id,
            markdown,
            empty,
        } = this.props;
        const value = getFieldValue(id) ?? "";
        let text: React.ReactNode;
        if (empty) {
            text = t("Form_No_Field_Set");
        } else {
            text = markdown ? renderMarkdown(value) : value;
        }
        return <Text type={empty ? "secondary" : undefined}>{text}</Text>;
    };

    onLabelClick = () => {
        if (!this.state.editing) {
            this.setState({ editing: true }, this.giveFocus);
        }
    };

    onFocus = () => {
        if (!this.state.editing) {
            this.setState({ editing: true });
        }
    };

    onBlur = () => {
        if (this.state.editing) {
            this.setState({ editing: false });
        }
    };

    blur = () => {
        if (this.innerField.current) {
            this.innerField.current.blur();
        }
    };

    giveFocus = () => {
        // We know that there is something here that requires a delay (decorator/async validation)
        setTimeout(() => {
            if (this.innerField.current) {
                this.innerField.current.focus();
            }
        });
    };
}

interface EditStringInputProps extends EditProps<string | undefined> {
    empty?: boolean;
}

const EditStringInputForm = EditFormCreate<string, EditStringInputProps>()(
    CreateStringInput
);

const EditStringInput = EditInput<string, EditStringInputProps>(
    EditStringInputForm
);

interface StringInputCommonProps {
    label: string;
    markdown?: boolean;
}

type StringInputProps<ID extends string, R> =
    | ({ type: "READ_ONLY" } & StringInputCommonProps & {
              value: string | undefined;
          })
    | ({ type: "EDIT" } & EditStringInputProps & StringInputCommonProps)
    | ({ type: "CREATE" } & CreateStringInputProps<ID, R>);

class StringInput<ID extends string, R> extends React.PureComponent<
    StringInputProps<ID, R>
> {
    private createStringInput = React.createRef<CreateStringInput<ID, R>>();

    render = () => {
        const { label } = this.props;
        switch (this.props.type) {
            case "READ_ONLY": {
                const { value, markdown } = this.props;
                const valueIdentifier: string =
                    value === undefined ? "" : value;
                const displayValue: string =
                    valueIdentifier === ""
                        ? t("Form_No_Field_Set")
                        : valueIdentifier;
                return (
                    <BaseFormDisplay label={label}>
                        <Text
                            type={value === undefined ? "secondary" : undefined}
                        >
                            {markdown ? (
                                renderMarkdown(displayValue)
                            ) : (
                                <p>{displayValue}</p>
                            )}
                        </Text>
                    </BaseFormDisplay>
                );
            }
            case "EDIT": {
                const { value, type: _type, ...rest } = this.props;
                return (
                    <EditStringInput
                        {...rest}
                        value={value}
                        empty={value === undefined || value === ""}
                    />
                );
            }
            case "CREATE": {
                const { type: _type, ...rest } = this.props;
                return (
                    <CreateStringInput
                        ref={this.createStringInput}
                        creationMode
                        {...rest}
                    />
                );
            }
            default:
                return assertNever(this.props);
        }
    };

    giveFocus = () => {
        if (this.createStringInput.current) {
            this.createStringInput.current.giveFocus();
        }
    };
}

export type {
    StringInputProps,
    CreateStringInputProps,
    EditStringInputProps,
    StringInputCommonProps,
};
export { StringInput };
